/**
 * Deterministic PRNG using xorshift32.
 * Same seed always produces same sequence — critical for P2P sync.
 *
 * Seeding uses a full-avalanche hash (xmur3 finaliser). The previous
 * shift-add string hash left seeds that differ only in a trailing character
 * ("<seed>-bot-3-7" vs "<seed>-bot-3-8") with nearly identical states, so
 * their first draws were correlated and per-turn bot decisions clustered.
 */
export class SeededRandom {
  private seed: number;

  constructor(seed: string | number) {
    this.seed = typeof seed === 'string' ? this.hashStr(seed) : SeededRandom.mixInt(seed);
  }

  private hashStr(s: string): number {
    let h = 1779033703 ^ s.length;
    for (let i = 0; i < s.length; i++) {
      h = Math.imul(h ^ s.charCodeAt(i), 3432918353);
      h = (h << 13) | (h >>> 19);
    }
    return SeededRandom.finalize(h);
  }

  private static mixInt(n: number): number {
    const i = Number.isFinite(n) ? Math.floor(Math.abs(n)) : 0;
    return SeededRandom.finalize((i | 0) ^ 0x9e3779b9);
  }

  /** murmur3 finaliser: every input bit affects every output bit. Never returns 0. */
  private static finalize(h: number): number {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h = (h ^ (h >>> 16)) >>> 0;
    return h || 1;
  }

  next(): number {
    // xorshift32 (Marsaglia) — logical shifts, state kept as uint32
    let x = this.seed;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.seed = x >>> 0;
    return this.seed / 4294967296;
  }

  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  getState(): number {
    return this.seed;
  }

  setState(state: number): void {
    this.seed = state;
  }

  static fromState(state: number): SeededRandom {
    const rng = new SeededRandom(1);
    rng.seed = state;
    return rng;
  }
}

/**
 * 64.9 (decision 115, R11 step 6): a draw keyed by integers (seed hash, unit, tick, purpose, index), in [0, 1).
 * No running state: the same keys give the same number on every engine, in any order, so a brain rebuilt from
 * the state draws what the one that ran would have. Integer mixing only (Math.imul, shifts), murmur3's finaliser.
 */
export function keyedRandom(...keys: number[]): number {
  let h = 0x9e3779b9 | 0;
  for (const k of keys) {
    h = Math.imul(h ^ (k | 0), 0x85ebca6b);
    h = (h << 13) | (h >>> 19);
    h = Math.imul(h, 0xc2b2ae35);
  }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** A string's 32-bit hash, for `keyedRandom`'s seed key. */
export function hashKey(s: string): number {
  let h = 1779033703 ^ s.length;
  for (let i = 0; i < s.length; i++) { h = Math.imul(h ^ s.charCodeAt(i), 3432918353); h = (h << 13) | (h >>> 19); }
  h = Math.imul(h ^ (h >>> 16), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  return (h ^ (h >>> 16)) | 0;
}

/**
 * R11 step 6 (lane B's note, ARCHITECTURE §5.5): a brain's search draws from a generator keyed by (seed, unit, basis
 * tick, purpose) — never a running generator carried between decisions — so a decision depends only on the state it
 * read and is the same in a replay, a worker or a rebuilt brain. The one helper for every brain's keyed search (the
 * action clock's gun crew today; the turn clock's search next).
 */
export function keyedGenerator(seed: string | number, unit: number, basis: number, purpose: string): SeededRandom {
  return new SeededRandom(`${seed}|${purpose}|${unit}|${basis}`);
}
